export enum MessageType {
  DIRECT_MESSAGE = 'direct_message',
  BROADCAST = 'broadcast',
  CONCERN = 'concern',
  GROUP_MESSAGE = 'group_message',
}

export type ConcernCategory =
  | 'academic'
  | 'behavior'
  | 'attendance'
  | 'health'
  | 'transport'
  | 'fees'
  | 'other';

export type ConcernPriority = 'low' | 'medium' | 'high';

export interface ConcernMetadata {
  subject: string;
  description: string;
  category: ConcernCategory;
  priority: ConcernPriority;
  student_id?: string;
}

export interface Concern extends Message {
  type: MessageType.CONCERN;
  concern_id?: string;
  concern_metadata?: ConcernMetadata;
}

export interface CreateConcernPayload {
  subject: string;
  description: string;
  category: ConcernCategory;
  priority: ConcernPriority;
  institute_ref: string;
  student_id?: string;
  sender_id?: string;
}

export interface ConcernThread {
  concern_id: string;
  concern: Concern;
  messages: Message[];
}

// User info as returned by backend
export interface MessageUserInfo {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture?: string; // Optional - for UI/mock use
}

// Message as returned by backend
export interface Message {
  id?: string; // Backend uses 'id'
  _id?: string; // Mocks use '_id'
  sender_id: string;
  receiver_id?: string;
  content: string;
  type: MessageType;
  institute_ref?: string;
  class_id?: string;
  section_id?: string;
  section?: string; // Mock support
  is_read: boolean;
  created_at: string | Date; // string from backend, Date from mocks
  updated_at?: string | Date; // Optional - mock support
  sender?: MessageUserInfo;
  receiver?: MessageUserInfo;
}

// Payload for sending a direct message
export interface SendMessagePayload {
  receiver_id: string;
  content: string;
  institute_ref: string;
  sender_id?: string; // Optional - for guardian use case
  type?: MessageType; // Optional - for explicit message type
}

// Response from sending a message
export interface SendMessageResponse {
  status: string;
  message: Message;
}

// Broadcast message payloads and targets
export interface BroadcastMessagePayload {
  content: string;
  institute_ref: string;
  class_id?: string;
  section_id?: string;
  sender_id?: string; // Optional - for guardian use case
  type?: MessageType; // Optional - for explicit message type
}

export interface BroadcastTarget {
  scope: 'institute' | 'class' | 'section';
  institute_id: string;
  class_id?: string;
  section?: string;
}

export interface SendBroadcastPayload {
  broadcastMessage: BroadcastMessagePayload;
  target: BroadcastTarget;
}

export interface BroadcastResponse {
  status: string;
  timestamp: string;
  scope: string;
  institute: string;
}

// WebSocket event names
export enum WebSocketEvent {
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  CONNECT_ERROR = 'connect_error',
  MESSAGE = 'message',
  BROADCAST = 'broadcast',
  GROUP_MESSAGE = 'newGroupMessage',
  ERROR = 'error',
  SEND_MESSAGE = 'sendMessage',
}

// WebSocket error response
export interface SocketErrorResponse {
  error?: string;
  message: string;
}

// Conversation from REST API
export interface Conversation {
  otherUser?: MessageUserInfo; // Optional for mock compatibility
  lastMessage?: Message; // Optional for mock compatibility
  unreadCount?: number; // Optional for mock compatibility
}

// Paginated message response
export interface PaginatedMessagesResponse {
  messages: Message[];
  total: number;
  page: number;
  limit: number;
}

// Unread count response
export interface UnreadCountResponse {
  unreadCount: number;
}

// Mark as read response
export interface MarkAsReadResponse {
  status: string;
  message: string;
}

// Mock support types - for test data generation
export interface MessageClassInfo {
  _id: string;
  level_name: string;
  class_code: string;
  grade: string;
  section: string;
}

export interface MessageInstituteInfo {
  _id: string;
  institute_name: string;
}

// Legacy support - keeping for existing code
export interface MessageWithRelations extends Message {
  class?: MessageClassInfo; // Mock support
  institute?: MessageInstituteInfo; // Mock support
}

export interface ConversationSummary extends Conversation {
  // Mock/Legacy support fields - snake_case versions (for existing UI code)
  conversation_id?: string;
  type?: MessageType;
  other_user?: MessageUserInfo;
  class_info?: MessageClassInfo;
  section?: string;
  last_message?: Message;
  unread_count?: number;
  last_activity?: string | Date;
}

export interface MessageListResponse {
  data: Message[];
  meta?: { 
    total: number; 
    page: number; 
    limit: number;
    total_pages?: number; // Mock support
    has_next?: boolean; // Mock support
    has_previous?: boolean; // Mock support
  };
}

export interface ConversationListResponse {
  data: Conversation[];
  meta?: { 
    total: number; 
    page: number; 
    limit: number;
    total_pages?: number; // Mock support
    has_next?: boolean; // Mock support
    has_previous?: boolean; // Mock support
  };
}

export interface CreateMessagePayload extends SendMessagePayload {}

// Query parameters for fetching messages
export interface MessageQueryParams {
  page?: number;
  limit?: number;
  receiver_id?: string;
  sender_id?: string;
  class_id?: string;
  type?: MessageType;
}

// Typing indicator payload
export interface TypingIndicatorPayload {
  userId: string;
  conversationId: string;
  isTyping: boolean;
}

// Type guards
export function isDirectMessage(
  message: Message
): message is Message & { receiver_id: string } {
  return message.type === MessageType.DIRECT_MESSAGE && !!message.receiver_id;
}

export function isBroadcastMessage(
  message: Message
): message is Message & { class_id: string } {
  return message.type === MessageType.BROADCAST && !!message.class_id;
}

export function isConcernMessage(message: Message): message is Concern {
  return message.type === MessageType.CONCERN;
}

export function isGroupMessage(
  message: Message
): message is Message & { group_id: string } {
  return message.type === MessageType.GROUP_MESSAGE && !!(message as any).group_id;
}

// Group chat interfaces
export interface GroupInfo {
  _id: string;
  name: string;
  description?: string;
  avatar_url?: string;
  group_type: 'custom' | 'class_group';
  institute_id: string;
  created_by: string;
  settings?: GroupSettings;
  is_active: boolean;
  max_members: number;
  created_at: string;
  updated_at: string;
}

export interface GroupSettings {
  allowMemberAdd?: boolean;
  onlyAdminsCanPost?: boolean;
  muteNotifications?: boolean;
}

export interface GroupMember {
  _id: string;
  group_id: string;
  user_id: string;
  role: 'admin' | 'member';
  joined_at: string;
  is_muted: boolean;
  last_read_at?: string;
}

export interface GroupConversation {
  group: GroupInfo;
  memberCount: number;
  userRole: 'admin' | 'member';
  lastMessageAt?: string;
  lastMessage?: Message;
  unreadCount?: number;
}

export interface GroupListResponse {
  groups: GroupConversation[];
  total: number;
}

export interface GroupMessageListResponse {
  data: Message[];
  meta?: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface CreateGroupPayload {
  name: string;
  description?: string;
  avatar_url?: string;
  group_type?: 'custom' | 'class_group';
  institute_id: string;
  initial_members?: { user_id: string; role?: 'admin' | 'member' }[];
  settings?: GroupSettings;
}

export interface SendGroupMessagePayload {
  group_id: string;
  content: string;
}
